import {
  AgxBadge,
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  formatDate,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2';
import {
  StaffPreviewLinkedDatesRequestSourceDateType,
  type LinkedDateImpact,
} from '@urbanx/agx.campaigns.client';
import {
  ContractInformationTaskConflictImpact,
  type ContractInformationTaskConflict,
} from '@urbanx/agx.activities.client';
import './LinkedDateWarningModal.scss';

export interface CompletedTaskConflict {
  taskName: string;
  impactLabel: string;
  canOverride: boolean;
}

const getTaskConflictImpactLabel = (conflict: ContractInformationTaskConflict) => {
  if (!conflict.impact) throw new Error('Completed task conflict impact is required');

  switch (conflict.impact) {
    case ContractInformationTaskConflictImpact.DueDateChange:
      return `Due date: ${conflict.currentDueUtc ? formatDate(conflict.currentDueUtc) : 'No due date'} → ${conflict.proposedDueUtc ? formatDate(conflict.proposedDueUtc) : 'No due date'}`;
    case ContractInformationTaskConflictImpact.Removal:
      return 'No longer applies to current contract information';
    case ContractInformationTaskConflictImpact.DependencyChange:
      return 'Dependencies differ from current contract information';
  }
};

export const presentCompletedTaskConflicts = (
  conflicts: readonly ContractInformationTaskConflict[]
): CompletedTaskConflict[] =>
  conflicts.map((conflict) => {
    if (!conflict.displayName || !conflict.impact || conflict.canOverride === undefined) {
      throw new Error('Completed task conflict details are required');
    }

    return {
      taskName: conflict.displayName,
      impactLabel: getTaskConflictImpactLabel(conflict),
      canOverride: conflict.canOverride,
    };
  });

interface LinkedDateWarningModalProps {
  impactedDates: LinkedDateImpact[];
  sourceDateTypes: StaffPreviewLinkedDatesRequestSourceDateType[];
  taskConflicts?: readonly CompletedTaskConflict[];
  onCancel: () => void;
  onConfirm: () => void;
}

const getSourceDateLabel = (sourceDateType: StaffPreviewLinkedDatesRequestSourceDateType) => {
  switch (sourceDateType) {
    case StaffPreviewLinkedDatesRequestSourceDateType.FinanceDate:
      return 'Finance date';
    case StaffPreviewLinkedDatesRequestSourceDateType.BuildingAndPestDate:
      return 'Building & Pest date';
    case StaffPreviewLinkedDatesRequestSourceDateType.ContractConditionChanges:
      return 'Unconditional date';
    case StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate:
      return 'Satisfaction of Special Condition';
    case StaffPreviewLinkedDatesRequestSourceDateType.ContractDate:
    default:
      return 'Contract date';
  }
};

export function LinkedDateWarningModal({
  impactedDates,
  sourceDateTypes,
  taskConflicts = [],
  onCancel,
  onConfirm,
}: LinkedDateWarningModalProps) {
  const sourceDateLabel = sourceDateTypes.map(getSourceDateLabel).join(' and ');
  const hasTaskConflicts = taskConflicts.length > 0;
  const canContinue = taskConflicts.every((conflict) => conflict.canOverride);

  return (
    <AgxModal
      id="linked-date-warning-modal"
      variant="withoutDivider"
      type="single"
      minWidth="600px"
      setIsDialogOpen={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      content={
        <AgxSpacer
          direction="vertical"
          gap="spacing-3xl"
          className="linked-date-warning-modal-content"
        >
          <AgxSpacer
            direction="vertical"
            gap="spacing-2xl"
            className="linked-date-warning-modal-header"
          >
            <AgxSpacer
              direction="horizontal"
              contentAlignment="center"
              contentJustification="center"
              className="linked-date-warning-modal-icon"
            >
              <Icon_AlertCircleOutline type="Danger" height={40} width={40} />
            </AgxSpacer>
            <AgxTypography
              id="linked-date-warning-modal-title"
              type="body_ui_strong"
              size="medium"
              color="var(--text-primary)"
            >
              {impactedDates.length > 0
                ? `Are you sure you want to change ${sourceDateLabel}?`
                : 'This change conflicts with completed tasks'}
            </AgxTypography>
            {impactedDates.length > 0 && (
              <AgxTypography
                id="linked-date-warning-modal-description"
                type="body_ui"
                size="small"
                color="var(--text-secondary)"
              >
                If you change this date, the following due dates will also be affected:
              </AgxTypography>
            )}
          </AgxSpacer>
          {impactedDates.length > 0 && (
            <AgxSpacer
              direction="vertical"
              gap="spacing-lg"
              className="linked-date-warning-modal-list"
            >
              {impactedDates.map((impact) => (
                <AgxSpacer
                  key={impact.key}
                  direction="horizontal"
                  gap="spacing-sm"
                  contentAlignment="center"
                  className="linked-date-warning-modal-row"
                >
                  <AgxTypography
                    id={`linked-date-warning-modal-${impact.key}-label`}
                    type="body_ui"
                    size="small"
                    color="var(--text-primary)"
                  >
                    <span style={{ opacity: 0.7 }}>&bull; {impact.label}</span>
                  </AgxTypography>
                  <AgxBadge
                    type="colourfill"
                    size="md"
                    colour="grey"
                    text={impact.relativeDateText}
                  />
                </AgxSpacer>
              ))}
            </AgxSpacer>
          )}
          {hasTaskConflicts && (
            <AgxSpacer
              direction="vertical"
              gap="spacing-lg"
              className="linked-date-warning-modal-task-conflicts"
            >
              <AgxTypography
                id="linked-date-warning-modal-task-conflicts-title"
                type="body_ui_strong"
                size="small"
                color="var(--text-primary)"
              >
                Completed task conflicts
              </AgxTypography>
              <AgxTypography
                id="linked-date-warning-modal-task-conflicts-description"
                type="body_ui"
                size="small"
                color="var(--text-secondary)"
              >
                These completed tasks stay unchanged. This change will make the current contract
                information differ from their historical details:
              </AgxTypography>
              <AgxSpacer direction="vertical" gap="spacing-lg">
                {taskConflicts.map((conflict, index) => (
                  <AgxSpacer
                    key={`${conflict.taskName}-${conflict.impactLabel}`}
                    direction="horizontal"
                    gap="spacing-sm"
                    contentAlignment="center"
                    className="linked-date-warning-modal-row"
                  >
                    <AgxTypography
                      id={`linked-date-warning-modal-task-${index}-label`}
                      type="body_ui"
                      size="small"
                      color="var(--text-primary)"
                    >
                      <span style={{ opacity: 0.7 }}>&bull; {conflict.taskName}</span>
                    </AgxTypography>
                    <AgxBadge
                      type="colourfill"
                      size="md"
                      colour={conflict.canOverride ? 'grey' : 'red'}
                      text={conflict.impactLabel}
                    />
                  </AgxSpacer>
                ))}
              </AgxSpacer>
            </AgxSpacer>
          )}
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer direction="horizontal" contentJustification="center" gap="spacing-lg" fill>
          <AgxButton
            id="linked-date-warning-cancel"
            variant="hollow"
            size="large"
            text="Cancel"
            stretch
            onClick={onCancel}
          />
          {canContinue && (
            <AgxButton
              id="linked-date-warning-confirm"
              variant="primary"
              size="large"
              text={hasTaskConflicts ? 'Continue anyway' : 'Confirm and edit'}
              danger
              stretch
              onClick={onConfirm}
            />
          )}
        </AgxSpacer>
      }
      actionButtonContainerStyle="withDivider"
    />
  );
}
